import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader, X, Clock, Users, Percent, Gift, Edit, Calendar } from "lucide-react";
import ProductCreateModal from "@/modals/ProductCreateModal";
import { 
  getSellerProducts,
  scheduleLiveStream,
  updateScheduledLive,
  sendLiveStreamNotifications
} from "@/services/liveStreamService";
import { Product } from "@/types/liveStreamTypes";
import ProductThumbnailGrid from "./ProductThumbnailGrid";
import DateTimePicker from "@/components/ui/date-time-picker";
import { useGetUserById } from "@/lib/react-query/queries";
import { Textarea } from "@/components/ui";

const ScheduleValidation = z.object({
  title: z.string().min(2, { message: "Title must be at least 2 characters." }),
  description: z.string().min(10, { message: "Description must be at least 10 characters." }).optional(),
  scheduledAt: z.date({ required_error: "Select a date and time." }),
  discount: z.object({
    maxUsers: z.number().min(1, { message: "At least 1 user required" }).max(100, { message: "Maximum 100 users allowed" }).optional(),
    percent: z.number().min(1, { message: "Minimum 1% discount" }).max(50, { message: "Maximum 50% discount allowed" }).optional(),
  }).optional(),
});

type ScheduleFormData = z.infer<typeof ScheduleValidation>;

interface ScheduleLiveModalProps {
  sellerId: string;
  onClose: () => void;
  onScheduleSuccess: () => void;
  editMode?: boolean;
  editData?: any;
}

export default function EnhancedScheduleLiveModal({ 
  sellerId, 
  onClose, 
  onScheduleSuccess, 
  editMode = false, 
  editData 
}: ScheduleLiveModalProps) {
  const { toast } = useToast();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscountPreview, setShowDiscountPreview] = useState(false);

  const { data: userData } = useGetUserById(sellerId || "");

  const form = useForm<ScheduleFormData>({
    resolver: zodResolver(ScheduleValidation),
    defaultValues: {
      title: editMode ? editData?.title || "" : "",
      description: editMode ? editData?.description || "" : "",
      scheduledAt: editMode ? new Date(editData?.scheduledAt) || new Date() : new Date(),
      discount: editMode && editData?.discountPercent && editData?.discountMaxUsers ? {
        percent: editData.discountPercent,
        maxUsers: editData.discountMaxUsers
      } : undefined,
    },
  });

  const watchedDiscount = form.watch("discount");

  // Show discount preview when both values are filled
  useEffect(() => {
    setShowDiscountPreview(
      Boolean(watchedDiscount?.maxUsers && watchedDiscount?.percent)
    );
  }, [watchedDiscount]);

  // Pre-populate data in edit mode
  useEffect(() => {
    if (editMode && editData) {
      setSelectedProducts(editData.products || []);
    }
  }, [editMode, editData]);

  // Fetch products when component mounts
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await getSellerProducts(sellerId);
        setAvailableProducts(response as Product[]);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setAvailableProducts([]);
        toast({
          title: "Error",
          description: "Failed to load products",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [sellerId, toast]);

  const handleProductSelect = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId) 
        : [...prev, productId]
    );
  };

  const calculateDiscountValue = () => {
    if (!watchedDiscount?.maxUsers || !watchedDiscount?.percent || selectedProducts.length === 0) {
      return 0;
    }

    // Calculate average discount value based on selected products
    const selectedProductsData = availableProducts.filter(p => selectedProducts.includes(p.$id));
    if (selectedProductsData.length === 0) return 0;
    
    const averagePrice = selectedProductsData.reduce((sum, product) => sum + parseFloat(product.price), 0) / selectedProductsData.length;
    
    return (averagePrice * watchedDiscount.percent) / 100;
  };

  const onSubmit = async (values: ScheduleFormData) => {
    if (selectedProducts.length === 0) {
      toast({ title: "Error", description: "Select at least 1 product.", variant: "destructive" });
      return;
    }

    if (!userData) {
      toast({ title: "Error", description: "User data not loaded.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean up discount object
      let cleanDiscount: { maxUsers: number; percent: number } | undefined;
      
      if (values.discount?.maxUsers && values.discount?.percent) {
        cleanDiscount = {
          maxUsers: values.discount.maxUsers,
          percent: values.discount.percent,
        };
      }

      if (editMode && editData) {
        // Update existing scheduled stream
        await updateScheduledLive(editData.$id, {
          title: values.title,
          description: values.description,
          products: selectedProducts,
          scheduledAt: values.scheduledAt.toISOString(),
          discount: cleanDiscount,
        });

        // Send notification about schedule change
        try {
          await sendLiveStreamNotifications(
            sellerId, 
            `Updated: ${values.title}`, 
            userData.name
          );
        } catch (notificationError) {
          console.error("Failed to send update notifications:", notificationError);
        }

        toast({
          title: "Stream Updated! ✏️",
          description: "Your followers have been notified about the changes.",
          variant: "default",
        });
      } else {
        // Schedule new live stream
        await scheduleLiveStream({
          sellerId,
          title: values.title,
          description: values.description || "",
          products: selectedProducts,
          scheduledAt: values.scheduledAt.toISOString(),
          discount: cleanDiscount,
        });

        // Send notifications to followers
        try {
          await sendLiveStreamNotifications(sellerId, values.title, userData.name);
        } catch (notificationError) {
          console.error("Failed to send notifications:", notificationError);
        }

        toast({
          title: "Live Stream Scheduled! 🎉",
          description: cleanDiscount 
            ? `Early bird discount: First ${cleanDiscount.maxUsers} buyers get ${cleanDiscount.percent}% off!`
            : "Your live stream has been scheduled successfully.",
          variant: "default",
        });
      }

      onScheduleSuccess();
      onClose();
    } catch (error) {
      console.error("Schedule error:", error);
      toast({ 
        title: "Error", 
        description: editMode ? "Failed to update schedule." : "Failed to schedule.", 
        variant: "destructive" 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateDisabled = (date: Date): boolean => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    return compareDate < today;
  };

  const handleProductCreated = (newProduct: any) => {
    // Create a Product object that matches your Product type from types/liveStreamTypes
    const productToAdd: Product = {
      $id: newProduct.$id || newProduct.id || Date.now().toString(),
      title: newProduct.title,
      price: newProduct.price,
      description: newProduct.description,
      images: newProduct.images || [],
      imageIds: newProduct.imageIds || [],
      minQuantity: newProduct.minQuantity || "1",
      sellerId: newProduct.sellerId,
      status: newProduct.status || "active",
      $collectionId: newProduct.$collectionId || "",
      $databaseId: newProduct.$databaseId || "",
      $createdAt: newProduct.$createdAt || new Date().toISOString(),
      $updatedAt: newProduct.$updatedAt || new Date().toISOString(),
      $permissions: newProduct.$permissions || [],
    };
    
    setAvailableProducts(prev => [...prev, productToAdd]);
    setSelectedProducts(prev => [...prev, productToAdd.$id]);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center mb-10 justify-center p-4 z-50">
      <div className="bg-dark-2 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="h3-bold flex items-center">
            {editMode ? (
              <>
                <Edit className="w-5 h-5 mr-2" />
                Edit Scheduled Stream
              </>
            ) : (
              <>
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Live Stream
              </>
            )}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isSubmitting}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {editMode && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center text-blue-700">
              <Edit className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">
                Editing scheduled stream. Changes will notify your followers.
              </span>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 text-gray-700">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-off-white">Stream Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Flash Sale!" {...field} disabled={isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-off-white">Stream Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="you can keep your terms and conditions here in short."
                      {...field} 
                      disabled={isSubmitting}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="scheduledAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-off-white flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    {editMode ? 'Update Date & Time' : 'Date & Time'}
                  </FormLabel>
                  <FormControl>
                    <DateTimePicker
                      date={field.value}
                      onDateChange={(date) => field.onChange(date)}
                      disabled={isDateDisabled}
                      placeholder="Select live stream date and time"
                    />
                  </FormControl>
                  {editMode && (
                    <p className="text-xs text-yellow-600 mt-1">
                      ⚠️ Changing the time will send notifications to all your followers
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <div className="flex justify-between items-center mb-2">
                <FormLabel className="text-off-white">Products</FormLabel>
                <Button 
                  type="button" 
                  size="sm" 
                  onClick={() => setShowProductModal(true)}
                  disabled={isSubmitting}
                >
                  Add New
                </Button>
              </div>
              {isLoading ? (
                <div className="flex justify-center py-4">
                  <Loader className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <ProductThumbnailGrid
                  products={availableProducts}
                  selectedIds={selectedProducts}
                  onSelect={handleProductSelect}
                />
              )}
            </div>

            {/* Enhanced Discount Section */}
            <div className="bg-gradient-to-r from-orange-50 to-rose rounded-lg p-4 border border-orange-200">
              <FormField
                control={form.control}
                name="discount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-800 flex items-center text-lg font-semibold">
                      <Gift className="w-5 h-5 mr-2 text-orange-600" />
                      Early-Bird Discount (Optional)
                    </FormLabel>
                    <p className="text-sm text-gray-600 mb-3">
                      Create FOMO! Offer exclusive discounts to first few buyers during your live stream.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 flex items-center mb-1">
                          <Users className="w-3 h-3 mr-1" />
                          First X buyers
                        </label>
                        <Input
                          type="number"
                          placeholder="e.g., 10"
                          min="1"
                          max="100"
                          disabled={isSubmitting}
                          value={field.value?.maxUsers || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                            field.onChange({ 
                              ...field.value, 
                              maxUsers: value 
                            });
                          }}
                          className="text-center font-semibold"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 flex items-center mb-1">
                          <Percent className="w-3 h-3 mr-1" />
                          Discount %
                        </label>
                        <Input
                          type="number"
                          placeholder="e.g., 20"
                          min="1"
                          max="50"
                          disabled={isSubmitting}
                          value={field.value?.percent || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
                            field.onChange({ 
                              ...field.value, 
                              percent: value 
                            });
                          }}
                          className="text-center font-semibold"
                        />
                      </div>
                    </div>

                    {/* Discount Preview */}
                    {showDiscountPreview && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-orange-300">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-700">
                            <span className="font-semibold text-orange-600">
                              First {watchedDiscount?.maxUsers} buyers
                            </span>
                            <span> get </span>
                            <span className="font-semibold text-green-600">
                              {watchedDiscount?.percent}% OFF
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Avg. savings</div>
                            <div className="font-bold text-green-600">
                              ₹{calculateDiscountValue().toFixed(0)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-2 text-xs text-gray-600 bg-yellow-50 p-2 rounded">
                          💡 <strong>Pro tip:</strong> This will create urgency and boost early sales during your live stream!
                        </div>
                      </div>
                    )}
                    
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={selectedProducts.length === 0 || isSubmitting}
                className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    {editMode ? 'Updating...' : 'Scheduling...'}
                  </>
                ) : (
                  <>
                    {editMode ? (
                      <>
                        <Edit className="h-4 w-4 mr-2" />
                        Update Stream
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4 mr-2" />
                        Schedule Stream
                      </>
                    )}
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>

        {showProductModal && (
          <ProductCreateModal
            sellerId={sellerId}
            onClose={() => setShowProductModal(false)}
            onProductCreated={handleProductCreated}
          />
        )}
      </div>
    </div>
  );
}